import { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

interface Message {
  id: number;
  role: 'ai' | 'user';
  text: string;
  actions?: { label: string; icon: string }[];
}

let msgId = 0;

export default function StudioView() {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: ++msgId,
      role: 'ai',
      text: 'Welcome to your studio! Your site "Fresh Bakery" is live. I noticed a few things that could improve it:',
      actions: [
        { label: 'Improve contrast', icon: 'palette' },
        { label: 'Add reviews section', icon: 'star' },
        { label: 'Speed up images', icon: 'speed' },
      ],
    },
  ]);
  const [inputVal, setInputVal] = useState('');
  const [typing, setTyping] = useState(false);
  const [viewport, setViewport] = useState<'mobile' | 'desktop' | 'wide'>('desktop');
  const [version, setVersion] = useState('v3 (latest)');
  const [versionOpen, setVersionOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'chat' | 'content' | 'design' | 'settings'>('chat');
  const chatEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
  }, []);

  useEffect(scrollToBottom, [messages, typing, scrollToBottom]);

  const addAiReply = useCallback((text: string, actions?: Message['actions']) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      setMessages((prev) => [...prev, { id: ++msgId, role: 'ai', text, actions }]);
    }, 1200);
  }, []);

  const handleAction = (label: string) => {
    setMessages((prev) => [...prev, { id: ++msgId, role: 'user', text: label }]);
    addAiReply(
      `Done! I've applied "${label}" and saved a backup (v4). The preview has been updated. Take a look!`,
      [
        { label: 'Undo this change', icon: 'undo' },
        { label: 'Make more changes', icon: 'edit' },
      ],
    );
    setVersion('v4 (latest)');
  };

  const handleSend = () => {
    if (!inputVal.trim()) return;
    const text = inputVal.trim();
    setInputVal('');
    setMessages((prev) => [...prev, { id: ++msgId, role: 'user', text }]);
    addAiReply(
      "I'll work on that right now. Saving a backup first, then applying your changes. You'll see the preview update in a moment.",
    );
  };

  const versions = ['v3 (latest)', 'v2 — added menu', 'v1 — initial build'];

  const previewWidths: Record<string, string> = {
    mobile: 'max-w-[320px]',
    desktop: 'max-w-full',
    wide: 'max-w-full',
  };

  return (
    <div className="h-screen bg-base flex flex-col overflow-hidden">
      {/* Top bar */}
      <div className="h-12 flex items-center justify-between px-4 border-b border-zinc-800 bg-zinc-950/80 shrink-0 gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/mockups" className="text-orange-500 font-bold text-sm shrink-0 hover:text-orange-400 transition">
            WiserWeb
          </Link>
          <span className="text-zinc-700">|</span>
          <span className="text-sm font-medium text-white truncate">Fresh Bakery</span>
          <span className="hidden sm:inline-flex items-center gap-1 text-emerald-400 text-xs shrink-0">
            <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
            Live
          </span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {/* Version selector */}
          <div className="relative">
            <button
              onClick={() => setVersionOpen(!versionOpen)}
              className="flex items-center gap-1 px-2 py-1 text-xs text-zinc-400 hover:text-white bg-zinc-800/60 border border-zinc-700/50 rounded transition"
            >
              <span className="material-icons text-xs">history</span>
              <span className="hidden sm:inline">{version}</span>
              <span className="material-icons text-xs">expand_more</span>
            </button>
            {versionOpen && (
              <div className="absolute right-0 top-full mt-1 w-48 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50">
                {versions.map((v) => (
                  <button
                    key={v}
                    onClick={() => {
                      setVersion(v);
                      setVersionOpen(false);
                    }}
                    className={`w-full text-left px-3 py-2 text-xs transition ${
                      version === v
                        ? 'text-orange-400 bg-orange-500/10'
                        : 'text-zinc-400 hover:bg-zinc-800'
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button className="px-3 py-1.5 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-semibold rounded-lg hover:shadow-glow transition">
            <span className="material-icons text-sm mr-1 align-middle">rocket_launch</span>
            Publish
          </button>
        </div>
      </div>

      {/* Main area — split */}
      <div className="flex-1 flex min-h-0">
        {/* Left — Chat + tools */}
        <div className="w-full md:w-[380px] lg:w-[420px] border-r border-zinc-800 flex flex-col shrink-0">
          {/* Tool tabs */}
          <div className="flex border-b border-zinc-800 shrink-0">
            {(
              [
                { id: 'chat', icon: 'chat', label: 'AI Chat' },
                { id: 'content', icon: 'edit_note', label: 'Content' },
                { id: 'design', icon: 'palette', label: 'Design' },
                { id: 'settings', icon: 'tune', label: 'Settings' },
              ] as const
            ).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1 py-2.5 text-xs font-medium transition border-b-2 ${
                  activeTab === tab.id
                    ? 'border-orange-500 text-orange-400'
                    : 'border-transparent text-zinc-500 hover:text-zinc-300'
                }`}
              >
                <span className="material-icons text-sm">{tab.icon}</span>
                <span className="hidden lg:inline">{tab.label}</span>
              </button>
            ))}
          </div>

          {activeTab === 'chat' ? (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-0">
                {messages.map((m) => (
                  <div
                    key={m.id}
                    className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[90%] rounded-2xl px-3 py-2.5 ${
                        m.role === 'user'
                          ? 'bg-orange-500/15 border border-orange-500/30 rounded-br-md'
                          : 'bg-zinc-800/60 border border-zinc-700/50 rounded-bl-md'
                      }`}
                    >
                      <p
                        className={`text-sm ${
                          m.role === 'user' ? 'text-orange-200' : 'text-zinc-300'
                        }`}
                      >
                        {m.text}
                      </p>
                      {m.actions && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {m.actions.map((a) => (
                            <button
                              key={a.label}
                              onClick={() => handleAction(a.label)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 bg-zinc-700/50 hover:bg-orange-500/20 border border-zinc-600 hover:border-orange-500/50 rounded-md text-[11px] text-zinc-300 hover:text-orange-300 transition"
                            >
                              <span className="material-icons text-xs">{a.icon}</span>
                              {a.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {typing && (
                  <div className="flex justify-start">
                    <div className="bg-zinc-800/60 border border-zinc-700/50 rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex gap-1">
                        <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" />
                        <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 border-t border-zinc-800 shrink-0">
                <div className="flex items-center gap-2 bg-zinc-800/60 border border-zinc-700/50 rounded-lg px-3">
                  <input
                    type="text"
                    value={inputVal}
                    onChange={(e) => setInputVal(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                    placeholder="Ask anything about your site..."
                    className="flex-1 bg-transparent py-2.5 text-sm text-white placeholder:text-zinc-600 outline-none"
                  />
                  <button onClick={handleSend} className="text-orange-400 hover:text-orange-300 transition">
                    <span className="material-icons text-lg">send</span>
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center">
                <span className="material-icons text-4xl text-zinc-700 mb-3 block">
                  {activeTab === 'content' ? 'edit_note' : activeTab === 'design' ? 'palette' : 'tune'}
                </span>
                <p className="text-sm text-zinc-500">
                  {activeTab === 'content' && 'Edit your text, images, and media here'}
                  {activeTab === 'design' && 'Colors, fonts, and layout options'}
                  {activeTab === 'settings' && 'Domain, SEO, and site configuration'}
                </p>
                <p className="text-xs text-zinc-600 mt-2">(Mockup placeholder)</p>
              </div>
            </div>
          )}
        </div>

        {/* Right — Live preview */}
        <div className="hidden md:flex flex-1 flex-col bg-zinc-950/30 min-w-0">
          {/* Viewport controls */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800/50 shrink-0">
            <span className="text-xs text-zinc-600">Live Preview</span>
            <div className="flex gap-1 bg-zinc-800/50 rounded-lg p-0.5">
              {(
                [
                  { id: 'mobile', icon: 'phone_iphone' },
                  { id: 'desktop', icon: 'laptop' },
                  { id: 'wide', icon: 'monitor' },
                ] as const
              ).map((vp) => (
                <button
                  key={vp.id}
                  onClick={() => setViewport(vp.id)}
                  className={`p-1.5 rounded transition ${
                    viewport === vp.id
                      ? 'bg-zinc-700 text-orange-400'
                      : 'text-zinc-500 hover:text-zinc-300'
                  }`}
                >
                  <span className="material-icons text-sm">{vp.icon}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Preview area */}
          <div className="flex-1 flex items-start justify-center p-6 overflow-y-auto">
            <div className={`w-full ${previewWidths[viewport]} transition-all duration-300`}>
              {/* Browser chrome */}
              <div className="bg-zinc-800/80 border border-zinc-700/60 rounded-xl overflow-hidden shadow-2xl">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-700/40 bg-zinc-900/60">
                  <div className="flex gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                    <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                  </div>
                  <div className="flex-1 flex items-center gap-1.5 bg-zinc-800/80 rounded-md px-2.5 py-1">
                    <span className="material-icons text-green-400 text-xs">lock</span>
                    <span className="text-xs text-zinc-400 font-mono">fresh-bakery.wiserwebhost.com</span>
                  </div>
                </div>

                {/* Fake site */}
                <div className={`bg-zinc-900/40 ${viewport === 'mobile' ? 'p-3' : 'p-6'}`}>
                  {/* Hero */}
                  <div className="h-32 bg-gradient-to-br from-amber-800/30 to-orange-700/30 rounded-xl flex items-center justify-center mb-4">
                    <div className="text-center">
                      <p className="text-xl font-bold text-orange-200/80">Fresh Bakery</p>
                      <p className="text-xs text-orange-300/40 mt-1">Artisan breads & pastries</p>
                    </div>
                  </div>

                  {/* Nav placeholder */}
                  <div className="flex gap-3 mb-4">
                    {['Menu', 'About', 'Hours', 'Order'].map((n) => (
                      <span key={n} className="text-xs text-zinc-500 px-2 py-1 bg-zinc-800/40 rounded">
                        {n}
                      </span>
                    ))}
                  </div>

                  {/* Content blocks */}
                  <div className={`grid ${viewport === 'mobile' ? 'grid-cols-1' : 'grid-cols-2'} gap-3`}>
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="bg-zinc-800/30 rounded-lg p-3">
                        <div className="h-16 bg-zinc-700/20 rounded mb-2" />
                        <div className="h-2 bg-zinc-700/20 rounded w-3/4 mb-1" />
                        <div className="h-2 bg-zinc-700/20 rounded w-1/2" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
